import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SignupSuccess = () => {
  const [mounted, setMounted] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setMounted(true);
    
    // Animate progress bar
    const timer = setTimeout(() => {
      setProgress(100);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const steps = [
    { number: 1, title: 'Application Submitted', description: 'Your instructor application has been received', status: 'completed' },
    { number: 2, title: 'Under Review', description: 'Our team is reviewing your profile and video', status: 'current' },
    { number: 3, title: 'Approval', description: 'Get approved and start teaching', status: 'upcoming' }
  ];

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row bg-gradient-to-br from-green-50 via-white to-emerald-50">
      {/* Left Side - Visual Section */}
      <div className="lg:flex-1 relative bg-gradient-to-br from-green-500 via-green-600 to-emerald-700 order-2 lg:order-1">
        <div className="absolute inset-0 bg-black bg-opacity-10"></div>
        
        {/* Enhanced Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Subtle Floating Shapes */}
          <div className="absolute top-1/4 left-1/4 w-40 h-40 bg-white bg-opacity-10 rounded-full animate-float-slow"></div>
          <div className="absolute top-1/3 right-1/4 w-32 h-32 bg-white bg-opacity-10 rounded-full animate-float-medium"></div>
          <div className="absolute bottom-1/4 left-1/3 w-36 h-36 bg-white bg-opacity-15 rounded-full animate-float-slower"></div>
          
          {/* Subtle Geometric Patterns */}
          <div className="absolute top-20 right-20 w-20 h-20 border-2 border-white border-opacity-15 rounded-xl animate-spin-slow"></div>
          <div className="absolute bottom-20 left-20 w-16 h-16 border border-white border-opacity-10 rounded-lg animate-ping-slow"></div>

          {/* Celebration Confetti Effect */}
          <div className="absolute top-10 left-10 w-4 h-4 bg-yellow-400 rounded-full animate-bounce"></div>
          <div className="absolute top-20 right-32 w-3 h-3 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          <div className="absolute bottom-32 left-40 w-3 h-3 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          <div className="absolute top-40 right-20 w-4 h-4 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.6s' }}></div>
        </div>

        {/* Enhanced Content Overlay */}
        <div className="relative h-full flex items-center justify-center p-6 lg:p-12">
          <div className={`text-center text-white max-w-md transform transition-all duration-1000 ease-out ${
            mounted ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {/* Success Icon */}
            <div className="w-20 h-20 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto mb-6 backdrop-blur-md shadow-2xl animate-glow-success">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            {/* Enhanced Text Content */}
            <h2 className="text-2xl lg:text-3xl xl:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-emerald-100 leading-tight">
              Welcome to Our Teaching Community!
            </h2>
            
            <p className="text-sm lg:text-base text-emerald-100 mb-6 leading-relaxed font-light">
              Your journey to inspiring learners worldwide begins here
            </p>

            {/* Instructor Welcome Stats */}
            <div className="grid grid-cols-3 gap-4 lg:gap-6 mt-8">
              {[
                { number: '24-48', label: 'Hours Review', icon: '⏱️' },
                { number: '95%', label: 'Approval Rate', icon: '✅' },
                { number: '50K+', label: 'Students', icon: '🎓' }
              ].map((stat, index) => (
                <div 
                  key={stat.label}
                  className={`text-center transform transition-all duration-700 ease-out ${
                    mounted ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95'
                  }`}
                  style={{ animationDelay: `${index * 200 + 500}ms` }}
                >
                  <div className="text-xl mb-1">{stat.icon}</div>
                  <div className="text-lg lg:text-xl font-bold text-white">{stat.number}</div>
                  <div className="text-xs text-emerald-100 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Welcome Benefits */}
            <div className={`mt-8 space-y-3 transform transition-all duration-800 ease-out delay-700 ${
              mounted ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}>
              <div className="flex items-center space-x-3 text-emerald-100">
                <div className="w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm">Join 10,000+ successful instructors</span>
              </div>
              <div className="flex items-center space-x-3 text-emerald-100">
                <div className="w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm">Access to 1M+ eager learners</span>
              </div>
              <div className="flex items-center space-x-3 text-emerald-100">
                <div className="w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm">Comprehensive teaching resources</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Success Content */}
      <div className="flex-1 flex flex-col justify-center py-8 lg:py-12 px-6 sm:px-8 lg:px-12 bg-white order-1 lg:order-2">
        <div className="w-full max-w-md mx-auto">
          {/* Enhanced Logo and Header */}
          <div className="text-center mb-8">
            <div className={`flex items-center justify-center space-x-3 mb-6 transform transition-all duration-700 ease-out ${
              mounted ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95'
            }`}>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg animate-float-gentle">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-700 bg-clip-text text-transparent">
                  Pengravity
                </h1>
                <p className="text-xs text-gray-500 font-medium">Learning Management System</p>
              </div>
            </div>
          </div>

          {/* Success Content */}
          <div className={`transform transition-all duration-800 ease-out delay-300 ${
            mounted ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}>
            {/* Success Message */}
            <div className="text-center mb-8">
              <h2 className="text-2xl lg:text-3xl font-extrabold text-gray-900 mb-4">
                🎉 Application Received!
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Thank you for your interest in becoming an instructor with Pengravity. 
                We're excited to review your application!
              </p>
            </div>

            {/* Progress Steps */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 mb-6 shadow-lg">
  <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">
    What's Next?
  </h3>
  
  {/* Steps - Improved Design */}
  <div className="space-y-6">
    {steps.map((step, index) => (
      <div key={step.number} className="flex items-start space-x-4">
        {/* Step Indicator */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold relative ${
          step.status === 'completed' 
            ? 'bg-green-500 text-white shadow-lg' 
            : step.status === 'current'
            ? 'bg-white text-green-600 border-2 border-green-500 shadow-md'
            : 'bg-gray-100 text-gray-400'
        }`}>
          {step.status === 'completed' ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            step.number
          )}
          
          {/* Connecting Line */}
          {index < steps.length - 1 && (
            <div className={`absolute top-10 left-1/2 transform -translate-x-1/2 w-0.5 h-8 ${
              step.status === 'completed' ? 'bg-green-500' : 'bg-gray-200'
            }`}></div>
          )}
        </div>
        
        {/* Step Content */}
        <div className="flex-1 pt-1">
          <h4 className={`font-semibold text-base ${
            step.status === 'completed' ? 'text-green-700' : 
            step.status === 'current' ? 'text-gray-900' : 'text-gray-500'
          }`}>
            {step.title}
          </h4>
          <p className="text-sm text-gray-600 mt-1">{step.description}</p>
          
          {/* Current Step Highlight */}
          {step.status === 'current' && (
            <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
              <svg className="w-3 h-3 mr-1 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              In Progress
            </div>
          )}
        </div>
      </div>
    ))}
  </div>

  {/* Timeline Summary */}
  <div className="mt-6 p-4 bg-white rounded-lg border border-green-100">
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-600">Estimated completion:</span>
      <span className="font-semibold text-green-600">24-48 hours</span>
    </div>
  </div>
</div>

            {/* Important Information */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 mb-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center mt-0.5">
                  <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-yellow-800 mb-2">What to Expect Next</h4>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    <li>• Our team will review your profile and video within 24-48 hours</li>
                    <li>• You'll receive an email notification once approved</li>
                    <li>• After approval, you can login and start creating courses</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Encouragement Message */}
            <div className="text-center mb-6">
              <p className="text-gray-600 text-sm leading-relaxed">
                <span className="font-semibold text-green-600">Your expertise matters!</span>{' '}
                We can't wait to see the impact you'll make on our learning community. 
                Get ready to inspire the next generation of learners!
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              {/* <button className="w-full py-3 px-6 text-base font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-200 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-300 group">
                <div className="flex items-center justify-center space-x-2">
                  <span>Check Your Email</span>
                  <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </button> */}

              <Link 
                to="/" 
                className="block w-full py-3 px-6 text-base font-semibold text-green-600 bg-green-50 hover:bg-green-100 border border-green-200 rounded-xl shadow-sm hover:shadow-md transform hover:scale-105 active:scale-95 transition-all duration-300 text-center group"
              >
                <div className="flex items-center justify-center space-x-2">
                  <span>Return to Homepage</span>
                  <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
              </Link>
            </div>

            {/* Support Information */}
            <div className="mt-6 text-center">
  <p className="text-gray-600 text-sm">
    Questions? Contact our support team:{' '}
    <a 
      href="mailto:support@pengravity.com" 
      className="font-semibold text-green-600 hover:text-green-700 transition-colors duration-300 hover:underline"
    >
      pengravity@gmail.com
    </a>
  </p>
</div>
          </div>
        </div>
      </div>

      {/* Enhanced Custom CSS */}
      <style jsx>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(180deg); }
        }
        @keyframes float-medium {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-8px) scale(1.02); }
        }
        @keyframes float-slower {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        @keyframes float-gentle {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-3px) rotate(3deg); }
          66% { transform: translateY(-2px) rotate(-2deg); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 1; }
          75%, 100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 10px rgba(255, 255, 255, 0.2); }
          50% { box-shadow: 0 0 15px rgba(255, 255, 255, 0.3); }
        }
        @keyframes glow-success {
          0%, 100% { box-shadow: 0 0 20px rgba(255, 255, 255, 0.3); }
          50% { box-shadow: 0 0 30px rgba(255, 255, 255, 0.5); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-float-slow { animation: float-slow 10s ease-in-out infinite; }
        .animate-float-medium { animation: float-medium 8s ease-in-out infinite; }
        .animate-float-slower { animation: float-slower 12s ease-in-out infinite; }
        .animate-float-gentle { animation: float-gentle 6s ease-in-out infinite; }
        .animate-spin-slow { animation: spin-slow 25s linear infinite; }
        .animate-ping-slow { animation: ping-slow 4s cubic-bezier(0, 0, 0.2, 1) infinite; }
        .animate-glow { animation: glow 4s ease-in-out infinite; }
        .animate-glow-success { animation: glow-success 2s ease-in-out infinite; }
        .animate-bounce { animation: bounce 2s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default SignupSuccess;